import { ApolloProvider } from '@apollo/client';
import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { config } from 'src/config';
import { createClient } from 'src/shared/graphql/client';
import { loginPathWithRedirectTo } from 'src/shared/helpers/routing';
import { useClosure } from 'src/shared/hooks';
import { LOGIN_REDIRECT_EXCLUSIONS } from 'src/shared/route';

interface ApolloClientProps {
  children: ReactNode;
}

const ApolloClientProvider = ({ children }: ApolloClientProps) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const match = useRouteMatch(LOGIN_REDIRECT_EXCLUSIONS);

  const onUnauthenticated = useClosure((skipRedirect: boolean) => {
    if (!match && !skipRedirect) {
      history.push(loginPathWithRedirectTo(pathname));
    }
  });

  // Memoize the apollo client to prevent recreating it
  // every time the provider re-renders. For example, when
  // the client routes to different pages.
  const client = useMemo(
    () =>
      createClient({
        state: window.initialState,
        uri: config.GRAPHQL_URI,
        onUnauthenticated,
      }),
    [onUnauthenticated],
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { ApolloClientProvider };
